import {
  AdditiveBlending,
  BufferAttribute,
  Group,
  Matrix4,
  Mesh,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * STATIC BATCHING.
 *
 * The mall is hand-authored out of small boxes — that is the whole reason it
 * looks the way it does, and it is not something to give up. But it means the
 * building shell alone was five and a half thousand separate meshes sharing a
 * hundred and twenty-three materials, and three.js issues one draw call per
 * mesh. The map was costing about eighteen thousand draw calls to push a hundred
 * and forty thousand triangles, which is a ratio no GPU can save you from: the
 * frame was CPU-bound in the renderer's own loop and the frame rate had fallen to
 * fifteen. Triangle count was never the problem.
 *
 * This walks a finished subtree and rebuilds it as one mesh per (material,
 * spatial cell), baking each source mesh's world transform into a copy of its
 * geometry. Nothing about the look changes — same materials, same vertices, same
 * positions — but the draw count collapses to roughly the number of distinct
 * materials times the number of occupied cells.
 *
 * WHY A SPATIAL CELL AND NOT JUST THE MATERIAL. Merging is a trade: you buy draw
 * calls with frustum culling, because a merged mesh is drawn whole or not at all.
 * Merge the whole building per material and every frame draws every wall in the
 * mall including the ones behind you. Cells keep the batches local, so standing
 * in the food court still culls the plaza. `CELL` is deliberately coarse — the
 * point is to keep culling meaningful, not to recover it exactly.
 *
 * WHAT IS DELIBERATELY LEFT ALONE:
 *  - anything named, and everything under it. Names are how the sim, the QA
 *    harness and the interaction code find things; a shutter that has been baked
 *    into the wall behind it cannot be raised. Named subtrees get their own,
 *    smaller merge instead — see `freezeInterior`.
 *  - ordinary transparency. Its correctness depends on per-object draw order.
 *    ADDITIVE transparency is exempt from that exemption, because addition is
 *    commutative: see `isOrderFree`.
 *  - lights, cameras, sprites, skinned meshes, anything that is not a plain Mesh.
 * `shouldFreeze` is the single place that policy lives.
 */

/** Cell size in metres. Coarse on purpose — see the note on culling above. */
const CELL = 22;

export interface MergeReport {
  before: number;
  after: number;
  batches: number;
  skipped: number;
}

/**
 * "This subtree is posed from JavaScript, so do not touch inside it."
 *
 * Almost everything in the mall is found by name — the sim, the interaction
 * code and the QA harness all look objects up that way — which means an UNNAMED
 * mesh is one nothing can reach and therefore one nothing can animate. That is
 * what makes `freezeInterior` safe to apply to every named group by default
 * rather than by opt-in, and it is worth the whole difference: the loot
 * containers alone are seven hundred and fifty-five meshes that are named at the
 * top and completely anonymous underneath.
 *
 * The exception is a figure. A guard's forearm is unnamed and still moves twenty
 * times a second, because the rig holds a direct reference to it from the moment
 * it was constructed. Those two classes call this on their root; nothing else
 * needs to, and if a third ever does the symptom is unmissable — a person
 * standing in a T-pose sliding around the mall.
 */
export function markRig(o: Object3D): void {
  o.userData.rig = true;
}

/**
 * A container whose name marks a boundary rather than a thing.
 *
 * `root` is the map itself; `dress_*` is a store's set dressing. Both are named
 * so they can be found, but neither name is a promise that anything INSIDE them
 * stays addressable — the dressing layer says so in its own contract: nothing
 * there is interactive, registers with the sim, or carries a collider. Walking
 * the ancestor chain past one of these is what disqualified every cereal box in
 * the mall from batching.
 */
const isBoundary = (o: Object3D, root: Object3D) => o === root || o.name.startsWith('dress_');

/**
 * Blending that does not care what order it happens in.
 *
 * Ordinary alpha is `dst = src*a + dst*(1-a)`, which is a different picture
 * depending on which surface goes first, so three.js sorts transparent meshes
 * back to front every frame and merging two of them destroys the sort. Additive
 * is `dst = src + dst`, and addition commutes — so every light pool and wall wash
 * in the building can collapse into one mesh with no visual consequence
 * whatsoever. There were a hundred and seventy of them, each with its own
 * material, and they were the second largest source of draw calls in the map.
 */
const isOrderFree = (m: Material) => m.blending === AdditiveBlending && m.depthWrite === false;

/** Can this single mesh's geometry be baked into a shared batch at all? */
function bakeable(o: Object3D): o is Mesh {
  if (!(o as Mesh).isMesh) return false;
  const mesh = o as Mesh;
  if (Array.isArray(mesh.material)) return false;
  const mat = mesh.material as Material;
  if ((mat.transparent || mat.depthWrite === false) && !isOrderFree(mat)) return false;
  return Boolean(mesh.geometry?.attributes?.position);
}

/**
 * True if this mesh may be baked into a batch.
 *
 * Being named is disqualifying on its own, and so is living under something
 * named. It is a blunt rule and it leaves some merges on the table, but the
 * alternative is an allowlist that silently rots every time someone adds a prop
 * — and the failure mode of getting this wrong is a piece of the game world that
 * can no longer be found or moved, which is far more expensive than a few
 * thousand draw calls. The exception is a boundary container, which is named for
 * the QA harness's benefit and explicitly owns nothing addressable below it.
 */
function shouldFreeze(o: Object3D, root: Object3D): boolean {
  if (!bakeable(o)) return false;
  for (let a: Object3D | null = o; a && !isBoundary(a, root); a = a.parent) {
    if (a.name) return false;
  }
  return true;
}

/**
 * Give every geometry in a batch the same attribute set.
 *
 * `mergeGeometries` returns null the moment two inputs disagree about which
 * attributes exist — one box with UVs and one without is enough — and it does it
 * silently, so the symptom is a batch that quietly disappears rather than an
 * error. Normalising up front is cheaper than diagnosing that later.
 */
function conform(geo: BufferGeometry, attrs: string[]): BufferGeometry {
  const count = geo.attributes.position.count;
  for (const name of attrs) {
    if (geo.attributes[name]) continue;
    const size = name === 'uv' || name === 'uv1' ? 2 : 3;
    // Missing colour has to default to WHITE, not to zero. Every standard
    // material in this project sets `vertexColors: true` to carry the baked
    // contact shading, and a zero-filled colour attribute multiplies the albedo
    // out to pure black — the exact failure `auditVertexColors` exists to catch.
    const fill = name === 'color' ? 1 : 0;
    geo.setAttribute(name, new BufferAttribute(new Float32Array(count * size).fill(fill), size));
  }
  for (const name of Object.keys(geo.attributes)) {
    if (!attrs.includes(name)) geo.deleteAttribute(name);
  }
  return geo;
}

interface Bucket {
  mat: Material;
  geos: BufferGeometry[];
  shadow: boolean;
  recv: boolean;
  order: number;
}

/**
 * What makes two meshes mergeable.
 *
 * Indexed and non-indexed geometry cannot be merged together, so the two go in
 * different buckets rather than being converted — converting an indexed box to
 * non-indexed triples its vertex count for no benefit.
 *
 * The shadow flags are part of the identity, not something to reconcile
 * afterwards. They were being OR'd across the bucket, which meant one
 * shadow-casting shelf upgraded every flat card merged with it and the clothing
 * store came out eight points darker than it was authored. A merge that changes
 * the picture is not a merge, and `renderOrder` is in the key for the same
 * reason.
 */
function bucketKey(mesh: Mesh, cell: string): string {
  const flags = `${mesh.castShadow ? 'C' : ''}${mesh.receiveShadow ? 'R' : ''}`;
  return `${(mesh.material as Material).uuid}|${cell}|${mesh.geometry.index ? 'i' : 'n'}|${flags}|${mesh.renderOrder}`;
}

/** Add a mesh's geometry, already transformed into the batch's space, to its bucket. */
function bucket(buckets: Map<string, Bucket>, mesh: Mesh, key: string, xform: Matrix4): void {
  const geo = mesh.geometry.clone();
  geo.applyMatrix4(xform);
  // `applyMatrix4` transforms normals by the inverse-transpose, but only if a
  // normal attribute is present when it runs. Everything built through
  // `flatVertexColor` has one; this is for anything that arrives without.
  if (!geo.attributes.normal) geo.computeVertexNormals();

  let b = buckets.get(key);
  if (!b)
    buckets.set(
      key,
      (b = {
        mat: mesh.material as Material,
        geos: [],
        shadow: mesh.castShadow,
        recv: mesh.receiveShadow,
        order: mesh.renderOrder,
      }),
    );
  b.geos.push(geo);
}

/** Turn every bucket into as few meshes as possible and hang them off `out`. */
function flush(buckets: Map<string, Bucket>, out: Object3D): number {
  let made = 0;
  const emit = (geo: BufferGeometry, b: Bucket) => {
    const m = new Mesh(geo, b.mat);
    m.castShadow = b.shadow;
    m.receiveShadow = b.recv;
    m.renderOrder = b.order;
    out.add(m);
    made++;
  };
  for (const b of buckets.values()) {
    // A bucket of one is already one draw call. Merging it just loses its
    // bounding sphere's tightness for nothing.
    if (b.geos.length === 1) {
      emit(b.geos[0], b);
      continue;
    }
    const attrs = [...new Set(b.geos.flatMap((g) => Object.keys(g.attributes)))].sort();
    const merged = mergeGeometries(b.geos.map((g) => conform(g, attrs)));
    if (!merged) {
      // Keep the pieces rather than dropping them on the floor. A batch that
      // fails to merge is a performance miss; a batch that vanishes is a hole in
      // the building, and the two must never be confused.
      for (const g of b.geos) emit(g, b);
      continue;
    }
    merged.computeBoundingSphere();
    emit(merged, b);
  }
  return made;
}

/** Detach the originals once their geometry has been copied into a batch. */
function retire(doomed: Mesh[]): void {
  for (const mesh of doomed) {
    mesh.removeFromParent();
    mesh.geometry.dispose();
  }
}

const inverse = new Matrix4();
const relative = new Matrix4();

/**
 * Collapse the anonymous meshes inside ONE named group, in that group's own space.
 *
 * The top-level pass refuses to touch anything under a name, which is correct and
 * also leaves the single biggest pile of draw calls in the game untouched: a
 * stock shelf is one addressable `container` wrapped around forty-four completely
 * anonymous boxes of stock, and there are twenty-seven of them. Nothing can reach
 * those boxes — see `markRig` for why that is the whole argument — so they can
 * become one mesh per material without the group losing its name, its transform,
 * its position in the sim, or its named children.
 *
 * No spatial cell here: a container is two metres across, so culling within it
 * was never doing anything. The transform baked in is LOCAL to `root`, which is
 * what lets the group keep moving afterwards.
 */
export function freezeInterior(root: Object3D): number {
  if (!root.children.length) return 0;
  root.updateMatrixWorld(true);

  const buckets = new Map<string, Bucket>();
  const doomed: Mesh[] = [];
  inverse.copy(root.matrixWorld).invert();

  root.traverse((o) => {
    if (o === root || o.userData.rig || !bakeable(o)) return;
    // Stop at the first name on the way up: a named child stays its own object,
    // and so does everything hanging off it.
    for (let a: Object3D | null = o; a && a !== root; a = a.parent) if (a.name) return;
    bucket(buckets, o, bucketKey(o, 'local'), relative.multiplyMatrices(inverse, o.matrixWorld));
    doomed.push(o);
  });

  if (!doomed.length) return 0;
  const made = flush(buckets, root);
  retire(doomed);
  return doomed.length - made;
}

/**
 * Rebuild `root` in place with its static meshes batched. Returns what changed.
 *
 * Runs after the whole map is built, so it does not constrain how anything is
 * authored: builders stay free to emit one small mesh per object, which is what
 * keeps the world readable as code.
 */
export function mergeStatic(root: Object3D): MergeReport {
  root.updateMatrixWorld(true);

  const buckets = new Map<string, Bucket>();
  const doomed: Mesh[] = [];
  const interiors: Object3D[] = [];
  let seen = 0;
  let skipped = 0;

  root.traverse((o) => {
    // A rig poses its own parts, so the whole subtree is off limits — including
    // the part where we would otherwise recurse into it looking for interiors.
    if (o.userData.rig) {
      o.traverse((c) => {
        if ((c as Mesh).isMesh) {
          seen++;
          skipped++;
        }
      });
      return;
    }
    if (o.name && !isBoundary(o, root)) interiors.push(o);
    if (!(o as Mesh).isMesh) return;
    seen++;
    if (!shouldFreeze(o, root)) {
      skipped++;
      return;
    }
    const mesh = o as Mesh;
    const p = mesh.getWorldPosition(mesh.position.clone());
    const cell = `${Math.floor(p.x / CELL)},${Math.floor(p.y / CELL)},${Math.floor(p.z / CELL)}`;
    bucket(buckets, mesh, bucketKey(mesh, cell), mesh.matrixWorld);
    doomed.push(mesh);
  });

  const batch = new Group();
  batch.name = 'static-batch';
  const batches = flush(buckets, batch);
  retire(doomed);
  root.add(batch);

  // Second pass, over the subtrees the first one was right to refuse. Only the
  // outermost name matters — `freezeInterior` walks down from there itself — and
  // a group already swallowed by an ancestor's freeze has no parent any more.
  let saved = 0;
  for (const g of interiors) {
    if (g.parent) saved += freezeInterior(g);
  }

  return { before: seen, after: seen - doomed.length + batches - saved, batches, skipped };
}
